import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';
import type {
  LinearCatalogData,
  LinearTeamSummary,
  LinearWorkflowStateSummary,
  LinearUserSummary,
  LinearProjectSummary,
} from '@corner/shared';

// ─── Props ──────────────────────────────────────

interface LinearCreateIssueModalProps {
  onSave: (issueTitle: string, teamName: string) => void;
  onClose: () => void;
  getToken?: () => string | null;
  /** Pre-filled from the document/analysis */
  title?: string;
  /** Pre-filled description from the document */
  description?: string;
}

// ─── Priority definitions ───────────────────────

const PRIORITIES = [
  { value: 0, label: 'No priority' },
  { value: 4, label: 'Urgent' },
  { value: 1, label: 'High' },
  { value: 2, label: 'Medium' },
  { value: 3, label: 'Low' },
] as const;

function priorityLabel(value: number) {
  return PRIORITIES.find((p) => p.value === value)?.label ?? 'No priority';
}

// ─── State color map (maps Linear state types to dot colors) ──

function stateColor(type?: string | null): string {
  switch (type) {
    case 'backlog': return '#B8ACA2';
    case 'unstarted': return '#E2A746';
    case 'started': return '#D97645';
    case 'completed': return '#1B7A4B';
    case 'cancelled': return '#9A8E82';
    default: return '#B8ACA2';
  }
}

// ─── Priority icon SVG ─────────────────────────

function PriorityIcon({ priority, color }: { priority: number; color: string }) {
  // 0 = no priority, 4 = urgent, 1 = high, 2 = medium, 3 = low
  const bars = priority === 0 ? 0 : priority === 4 ? 3 : priority === 1 ? 3 : priority === 2 ? 2 : 1;
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
      <rect x="1" y="3" width="2.5" height="8" rx="0.8" fill={color} opacity={bars >= 3 ? 1 : 0.2} />
      <rect x="4.5" y="5.5" width="2.5" height="5.5" rx="0.8" fill={color} opacity={bars >= 2 ? 1 : 0.2} />
      <rect x="8" y="8" width="2.5" height="3" rx="0.8" fill={color} opacity={bars >= 1 ? 1 : 0.2} />
    </svg>
  );
}

// ─── Linear logo SVG ────────────────────────────

function LinearLogo() {
  return (
    <svg width="24" height="24" viewBox="0 0 100 100" fill="none" style={{ flexShrink: 0 }}>
      <path d="M1.225 61.523c-.2225-.9485.907-1.546 1.596-.857L39.334 97.178c.6889.689.091 1.819-.857 1.596C20.052 94.452 5.548 79.948 1.225 61.523Z" fill="#5E6AD2" />
      <path d="M.00189 46.889c-.1764.283.889.56.29.761L52.35 99.709c.2007.201.477.307.761.29 2.369-.1476 4.694-.46 6.962-.9259.764-.157 1.03-1.096.478-1.648L2.576 39.449c-.5519-.5519-1.491-.2863-1.648.478-.4659 2.269-.7783 4.593-.92588 6.962Z" fill="#5E6AD2" />
      <path d="M4.211 29.705c-.1665.374-.0817.811.208 1.1l64.776 64.776c.2894.289.726.374 1.1.208 1.786-.7956 3.517-1.693 5.186-2.684.552-.328.637-1.087.183-1.541L8.436 24.337c-.4541-.4541-1.213-.3689-1.541.183-.9913 1.668-1.888 3.399-2.684 5.186Z" fill="#5E6AD2" />
      <path d="M12.659 18.074c-.3701-.3701-.393-.9637-.0443-1.354C21.779 6.459 35.111 0 49.952 0 77.593 0 100 22.407 100 50.048c0 14.841-6.459 28.172-16.72 37.337-.3903.349-.984.326-1.354-.0443L12.659 18.074Z" fill="#5E6AD2" />
    </svg>
  );
}

// ─── Property type icons (from design) ──────────

function StatusIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" style={{ flexShrink: 0 }}>
      <circle cx="7.5" cy="7.5" r="6" stroke="#9A8E82" strokeWidth="1.2" />
      <circle cx="7.5" cy="7.5" r="3" fill="#9A8E82" />
    </svg>
  );
}

function PriorityLabelIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" style={{ flexShrink: 0 }}>
      <rect x="2" y="3" width="3" height="9" rx="1" fill="#9A8E82" />
      <rect x="6" y="6" width="3" height="6" rx="1" fill="#9A8E82" opacity="0.5" />
      <rect x="10" y="9" width="3" height="3" rx="1" fill="#9A8E82" opacity="0.3" />
    </svg>
  );
}

function AssigneeIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" style={{ flexShrink: 0 }}>
      <circle cx="7.5" cy="5.5" r="2.5" stroke="#9A8E82" strokeWidth="1.2" />
      <path d="M3 13C3 10.5 5 9 7.5 9C10 9 12 10.5 12 13" stroke="#9A8E82" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

function ProjectIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" style={{ flexShrink: 0 }}>
      <rect x="2" y="2" width="11" height="11" rx="2" stroke="#9A8E82" strokeWidth="1.2" />
      <path d="M5 6H10M5 9H8" stroke="#9A8E82" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

function LabelsIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" style={{ flexShrink: 0 }}>
      <path d="M2.5 8.5V3.5C2.5 3 3 2.5 3.5 2.5H8.5L12.5 6.5V11.5C12.5 12 12 12.5 11.5 12.5H6.5L2.5 8.5Z" stroke="#9A8E82" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="6" cy="6" r="1" fill="#9A8E82" />
    </svg>
  );
}

// ─── Main component ─────────────────────────────

export default function LinearCreateIssueModal({
  onSave,
  onClose,
  getToken,
  title = '',
  description = '',
}: LinearCreateIssueModalProps) {
  // Catalog data
  const [catalog, setCatalog] = useState<LinearCatalogData | null>(null);
  const [loading, setLoading] = useState(true);

  // Form state
  const [selectedTeam, setSelectedTeam] = useState<LinearTeamSummary | null>(null);
  const [issueTitle, setIssueTitle] = useState(title);
  const [issueDescription, setIssueDescription] = useState(description);
  const [selectedState, setSelectedState] = useState<LinearWorkflowStateSummary | null>(null);
  const [selectedPriority, setSelectedPriority] = useState<number>(2); // Medium default
  const [selectedAssignee, setSelectedAssignee] = useState<LinearUserSummary | null>(null);
  const [selectedProject, setSelectedProject] = useState<LinearProjectSummary | null>(null);
  const [saving, setSaving] = useState(false);

  // Dropdown states
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const headers = useCallback(() => {
    const token = getToken?.();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [getToken]);

  // Fetch catalog on mount
  useEffect(() => {
    setLoading(true);
    axios.get<LinearCatalogData>('/api/integrations/linear/catalog', { headers: headers() })
      .then((res) => {
        const data = res.data;
        setCatalog(data);
        // Auto-select first team
        if (data.teams.length > 0) {
          setSelectedTeam(data.teams[0]);
        }
      })
      .catch(() => {
        toast({ title: 'Failed to load', description: 'Could not fetch Linear teams.', variant: 'destructive' });
      })
      .finally(() => setLoading(false));
  }, [headers]);

  // Filter states/projects by selected team
  const teamStates = useMemo(() => {
    if (!catalog || !selectedTeam) return [];
    return catalog.states.filter((s) => s.teamId === selectedTeam.id || !s.teamId);
  }, [catalog, selectedTeam]);

  const teamProjects = useMemo(() => {
    if (!catalog || !selectedTeam) return [];
    return catalog.projects.filter((p) => p.teamIds.includes(selectedTeam.id));
  }, [catalog, selectedTeam]);

  const activeAssignees = useMemo(() => {
    if (!catalog) return [];
    return catalog.assignees.filter((a) => a.active !== false);
  }, [catalog]);

  // Reset state + project when team changes (they are team-scoped)
  useEffect(() => {
    if (teamStates.length > 0) {
      const toDo = teamStates.find((s) => s.type === 'unstarted');
      setSelectedState(toDo ?? teamStates[0]);
    } else {
      setSelectedState(null);
    }
    setSelectedProject(null);
  }, [teamStates]);

  const handleCreate = useCallback(async () => {
    if (!selectedTeam || !issueTitle.trim()) return;
    setSaving(true);
    try {
      await axios.post('/api/integrations/linear/issues', {
        title: issueTitle.trim(),
        description: issueDescription.trim() || undefined,
        teamId: selectedTeam.id,
        stateId: selectedState?.id,
        priority: selectedPriority,
        assigneeId: selectedAssignee?.id,
        projectId: selectedProject?.id,
      }, { headers: headers() });

      onSave(issueTitle.trim(), selectedTeam.name);
    } catch {
      toast({ title: 'Failed to create', description: 'Could not create Linear issue.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  }, [selectedTeam, issueTitle, issueDescription, selectedState, selectedPriority, selectedAssignee, selectedProject, headers, onSave]);

  const toggleDropdown = useCallback((name: string) => {
    setOpenDropdown((prev) => (prev === name ? null : name));
  }, []);

  return (
    <div style={MODAL_SHELL} onClick={() => setOpenDropdown(null)}>
      {/* Header */}
      <div style={HEADER}>
        <LinearLogo />
        <span style={{ fontSize: 15, fontWeight: 600, color: '#2E2318', flexGrow: 1, lineHeight: '18px' }}>Create Linear issue</span>
        <button type="button" onClick={onClose} style={CLOSE_BTN}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4 4L12 12M12 4L4 12" stroke="#9A8E82" strokeWidth="1.5" strokeLinecap="round" /></svg>
        </button>
      </div>
      <div style={{ width: '100%', height: 1, backgroundColor: '#EDE8E2', flexShrink: 0 }} />

      {loading ? (
        <div style={{ padding: '40px 24px', textAlign: 'center', fontSize: 13, color: '#9A8E82', fontFamily: F }}>Loading teams...</div>
      ) : !catalog || catalog.teams.length === 0 ? (
        <div style={{ padding: '40px 24px', textAlign: 'center', fontSize: 13, color: '#9A8E82', fontFamily: F }}>
          No teams found. Make sure Linear is connected in Settings.
        </div>
      ) : (
        <>
          {/* Team picker */}
          <div style={{ display: 'flex', flexDirection: 'column', padding: '16px 24px 0', gap: 6 }}>
            <span style={LABEL}>Team</span>
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); toggleDropdown('team'); }}
                style={TEAM_PICKER}
              >
                {selectedTeam && (
                  <div style={TEAM_BADGE}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: '#FFFFFF' }}>
                      {selectedTeam.key?.[0] ?? selectedTeam.name[0]}
                    </span>
                  </div>
                )}
                <span style={{ fontSize: 13, fontWeight: 400, color: '#2E2318', flexGrow: 1 }}>
                  {selectedTeam?.name ?? 'Select team'}
                </span>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0 }}>
                  <path d="M3 4.5L6 7.5L9 4.5" stroke="#9A8E82" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              {openDropdown === 'team' && catalog && (
                <div style={DROPDOWN} onClick={(e) => e.stopPropagation()}>
                  {catalog.teams.map((team) => (
                    <button
                      key={team.id}
                      type="button"
                      onClick={() => { setSelectedTeam(team); setOpenDropdown(null); }}
                      style={{ ...DROPDOWN_ITEM, fontWeight: team.id === selectedTeam?.id ? 500 : 400 }}
                    >
                      <div style={{ ...TEAM_BADGE, width: 16, height: 16 }}>
                        <span style={{ fontSize: 8, fontWeight: 700, color: '#FFFFFF' }}>{team.key?.[0] ?? team.name[0]}</span>
                      </div>
                      <span style={{ flex: 1 }}>{team.name}</span>
                      {team.id === selectedTeam?.id && <Checkmark />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Title */}
          <div style={{ display: 'flex', flexDirection: 'column', padding: '12px 24px 0', gap: 6 }}>
            <span style={LABEL}>Title</span>
            <input
              type="text"
              value={issueTitle}
              onChange={(e) => setIssueTitle(e.target.value)}
              style={TEXT_INPUT}
              placeholder="Issue title"
            />
          </div>

          {/* Description */}
          <div style={{ display: 'flex', flexDirection: 'column', padding: '12px 24px 0', gap: 6 }}>
            <span style={LABEL}>Description</span>
            <textarea
              value={issueDescription}
              onChange={(e) => setIssueDescription(e.target.value)}
              style={TEXTAREA}
              placeholder="Describe the issue..."
              rows={3}
            />
          </div>

          {/* Properties divider */}
          <div style={{ display: 'flex', alignItems: 'center', padding: '16px 24px 8px', gap: 8 }}>
            <span style={LABEL}>Properties</span>
            <div style={{ flexGrow: 1, height: 1, backgroundColor: '#EDE8E2' }} />
          </div>

          {/* Property rows */}
          <div style={{ display: 'flex', flexDirection: 'column', padding: '0 24px', gap: 8 }}>
            {/* Status */}
            <PropertyRowShell icon={<StatusIcon />} label="Status">
              <div style={{ position: 'relative', flex: 1 }}>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); toggleDropdown('status'); }}
                  style={PROP_VALUE_FILLED}
                >
                  <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: stateColor(selectedState?.type), flexShrink: 0 }} />
                  <span style={{ fontSize: 13, color: '#2E2318', flexGrow: 1 }}>{selectedState?.name ?? 'Select status'}</span>
                  <ChevronDown />
                </button>
                {openDropdown === 'status' && (
                  <div style={DROPDOWN} onClick={(e) => e.stopPropagation()}>
                    {teamStates.map((s) => (
                      <button key={s.id} type="button" onClick={() => { setSelectedState(s); setOpenDropdown(null); }} style={{ ...DROPDOWN_ITEM, fontWeight: s.id === selectedState?.id ? 500 : 400 }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: stateColor(s.type) }} />
                        <span style={{ flex: 1 }}>{s.name}</span>
                        {s.id === selectedState?.id && <Checkmark />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </PropertyRowShell>

            {/* Priority */}
            <PropertyRowShell icon={<PriorityLabelIcon />} label="Priority">
              <div style={{ position: 'relative', flex: 1 }}>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); toggleDropdown('priority'); }}
                  style={PROP_VALUE_FILLED}
                >
                  <PriorityIcon priority={selectedPriority} color="#D97645" />
                  <span style={{ fontSize: 13, color: '#2E2318', flexGrow: 1 }}>{priorityLabel(selectedPriority)}</span>
                  <ChevronDown />
                </button>
                {openDropdown === 'priority' && (
                  <div style={DROPDOWN} onClick={(e) => e.stopPropagation()}>
                    {PRIORITIES.map((p) => (
                      <button key={p.value} type="button" onClick={() => { setSelectedPriority(p.value); setOpenDropdown(null); }} style={{ ...DROPDOWN_ITEM, fontWeight: p.value === selectedPriority ? 500 : 400 }}>
                        <PriorityIcon priority={p.value} color={p.value === selectedPriority ? '#D97645' : '#9A8E82'} />
                        <span style={{ flex: 1 }}>{p.label}</span>
                        {p.value === selectedPriority && <Checkmark />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </PropertyRowShell>

            {/* Assignee */}
            <PropertyRowShell icon={<AssigneeIcon />} label="Assignee">
              <div style={{ position: 'relative', flex: 1 }}>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); toggleDropdown('assignee'); }}
                  style={selectedAssignee ? PROP_VALUE_FILLED : PROP_VALUE_EMPTY}
                >
                  {selectedAssignee ? (
                    <>
                      <div style={AVATAR}>
                        <span style={{ fontSize: 9, fontWeight: 600, color: '#FFFFFF' }}>
                          {initials(selectedAssignee.name)}
                        </span>
                      </div>
                      <span style={{ fontSize: 13, color: '#2E2318', flexGrow: 1 }}>{selectedAssignee.name}</span>
                      <ChevronDown />
                    </>
                  ) : (
                    <>
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 3v6M3 6h6" stroke="#B8ACA2" strokeWidth="1.2" strokeLinecap="round" /></svg>
                      <span style={{ fontSize: 13, color: '#B8ACA2' }}>Assign</span>
                    </>
                  )}
                </button>
                {openDropdown === 'assignee' && (
                  <div style={DROPDOWN} onClick={(e) => e.stopPropagation()}>
                    {/* Unassign option */}
                    <button type="button" onClick={() => { setSelectedAssignee(null); setOpenDropdown(null); }} style={{ ...DROPDOWN_ITEM, color: '#9A8E82' }}>
                      <span style={{ flex: 1 }}>Unassigned</span>
                      {!selectedAssignee && <Checkmark />}
                    </button>
                    {activeAssignees.map((a) => (
                      <button key={a.id} type="button" onClick={() => { setSelectedAssignee(a); setOpenDropdown(null); }} style={{ ...DROPDOWN_ITEM, fontWeight: a.id === selectedAssignee?.id ? 500 : 400 }}>
                        <div style={{ ...AVATAR, width: 18, height: 18 }}>
                          <span style={{ fontSize: 8, fontWeight: 600, color: '#FFFFFF' }}>{initials(a.name)}</span>
                        </div>
                        <span style={{ flex: 1 }}>{a.name ?? a.email ?? 'Unknown'}</span>
                        {a.id === selectedAssignee?.id && <Checkmark />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </PropertyRowShell>

            {/* Project */}
            <PropertyRowShell icon={<ProjectIcon />} label="Project">
              <div style={{ position: 'relative', flex: 1 }}>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); toggleDropdown('project'); }}
                  style={selectedProject ? PROP_VALUE_FILLED : PROP_VALUE_EMPTY}
                >
                  {selectedProject ? (
                    <>
                      <div style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: '#1B7A4B', flexShrink: 0 }} />
                      <span style={{ fontSize: 13, color: '#2E2318', flexGrow: 1 }}>{selectedProject.name}</span>
                      <ChevronDown />
                    </>
                  ) : (
                    <>
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 3v6M3 6h6" stroke="#B8ACA2" strokeWidth="1.2" strokeLinecap="round" /></svg>
                      <span style={{ fontSize: 13, color: '#B8ACA2' }}>Set project</span>
                    </>
                  )}
                </button>
                {openDropdown === 'project' && (
                  <div style={DROPDOWN} onClick={(e) => e.stopPropagation()}>
                    <button type="button" onClick={() => { setSelectedProject(null); setOpenDropdown(null); }} style={{ ...DROPDOWN_ITEM, color: '#9A8E82' }}>
                      <span style={{ flex: 1 }}>No project</span>
                      {!selectedProject && <Checkmark />}
                    </button>
                    {teamProjects.map((p) => (
                      <button key={p.id} type="button" onClick={() => { setSelectedProject(p); setOpenDropdown(null); }} style={{ ...DROPDOWN_ITEM, fontWeight: p.id === selectedProject?.id ? 500 : 400 }}>
                        <div style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: '#1B7A4B', flexShrink: 0 }} />
                        <span style={{ flex: 1 }}>{p.name}</span>
                        {p.id === selectedProject?.id && <Checkmark />}
                      </button>
                    ))}
                    {teamProjects.length === 0 && (
                      <div style={{ padding: '8px 12px', fontSize: 12, color: '#9A8E82', fontFamily: F }}>No projects for this team</div>
                    )}
                  </div>
                )}
              </div>
            </PropertyRowShell>

            {/* Labels (empty / future) */}
            <PropertyRowShell icon={<LabelsIcon />} label="Labels">
              <div style={PROP_VALUE_EMPTY}>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 3v6M3 6h6" stroke="#B8ACA2" strokeWidth="1.2" strokeLinecap="round" /></svg>
                <span style={{ fontSize: 13, color: '#B8ACA2' }}>Add labels</span>
              </div>
            </PropertyRowShell>
          </div>

          {/* AI hint */}
          <div style={AI_HINT}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
              <path d="M7 1L8.2 5.8L13 7L8.2 8.2L7 13L5.8 8.2L1 7L5.8 5.8Z" fill="#5E6AD2" opacity="0.7" />
            </svg>
            <span style={{ fontSize: 12, color: '#6B5E52', lineHeight: '17px', fontFamily: F }}>
              Fields were pre-filled from the document analysis. Edit any value before saving.
            </span>
          </div>

          {/* Footer */}
          <div style={FOOTER}>
            <button type="button" onClick={onClose} style={CANCEL_BTN}>Cancel</button>
            <button
              type="button"
              onClick={handleCreate}
              disabled={saving || !selectedTeam || !issueTitle.trim()}
              style={{ ...CREATE_BTN, opacity: saving || !selectedTeam || !issueTitle.trim() ? 0.5 : 1 }}
            >
              {saving ? 'Creating...' : 'Create issue'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Shared sub-components ──────────────────────

function PropertyRowShell({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
  return (
    <div style={PROP_ROW}>
      <div style={PROP_LABEL_COL}>
        {icon}
        <span style={{ fontSize: 13, color: '#6B5E52', lineHeight: '16px', fontFamily: F }}>{label}</span>
      </div>
      {children}
    </div>
  );
}

function ChevronDown() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ flexShrink: 0 }}>
      <path d="M2.5 3.75L5 6.25L7.5 3.75" stroke="#9A8E82" strokeWidth="1" strokeLinecap="round" />
    </svg>
  );
}

function Checkmark() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M3 7.5l3 3 5-6" stroke="#5E6AD2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function initials(name?: string | null): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name[0].toUpperCase();
}

// ─── Styles ─────────────────────────────────────

const F = '"Geist", system-ui, sans-serif';

const MODAL_SHELL: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  width: 460,
  maxHeight: '80vh',
  borderRadius: 14,
  overflowY: 'auto',
  overflowX: 'hidden',
  backgroundColor: '#FDFCFB',
  boxShadow: 'rgba(0,0,0,0.25) 0px 16px 60px, rgba(0,0,0,0.05) 0px 0px 0px 1px',
  fontFamily: F,
};

const HEADER: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  padding: '20px 24px 16px',
  gap: 12,
};

const CLOSE_BTN: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  display: 'flex',
};

const LABEL: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  color: '#9A8E82',
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  lineHeight: '14px',
  fontFamily: F,
};

const TEAM_PICKER: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  padding: '10px 12px',
  borderRadius: 8,
  border: 'none',
  backgroundColor: '#F5F2ED',
  cursor: 'pointer',
  fontFamily: F,
  textAlign: 'left',
  gap: 8,
};

const TEAM_BADGE: React.CSSProperties = {
  width: 18,
  height: 18,
  borderRadius: 4,
  backgroundColor: '#5E6AD2',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const TEXT_INPUT: React.CSSProperties = {
  padding: '10px 12px',
  borderRadius: 8,
  border: '1px solid #EDE8E2',
  backgroundColor: '#FFFFFF',
  fontSize: 13,
  fontWeight: 400,
  color: '#2E2318',
  fontFamily: F,
  lineHeight: '16px',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};

const TEXTAREA: React.CSSProperties = {
  padding: '10px 12px',
  borderRadius: 8,
  border: '1px solid #EDE8E2',
  backgroundColor: '#FFFFFF',
  fontSize: 13,
  fontWeight: 400,
  color: '#2E2318',
  fontFamily: F,
  lineHeight: '19px',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
  resize: 'vertical' as const,
  minHeight: 60,
};

const DROPDOWN: React.CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  marginTop: 4,
  maxHeight: 200,
  overflowY: 'auto',
  backgroundColor: '#FDFCFB',
  border: '1px solid #E4DDD4',
  borderRadius: 8,
  boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
  zIndex: 10,
  padding: 4,
};

const DROPDOWN_ITEM: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  padding: '8px 12px',
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  borderRadius: 6,
  fontFamily: F,
  fontSize: 13,
  color: '#2E2318',
  textAlign: 'left',
};

const PROP_ROW: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  height: 36,
  gap: 0,
};

const PROP_LABEL_COL: React.CSSProperties = {
  width: 120,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};

const PROP_VALUE_FILLED: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flex: 1,
  padding: '6px 10px',
  borderRadius: 6,
  backgroundColor: '#F5F2ED',
  border: 'none',
  cursor: 'pointer',
  fontFamily: F,
  textAlign: 'left',
  gap: 6,
};

const PROP_VALUE_EMPTY: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flex: 1,
  padding: '6px 10px',
  borderRadius: 6,
  border: '1px dashed #D4CAC0',
  background: 'none',
  gap: 6,
  cursor: 'pointer',
  fontFamily: F,
};

const AVATAR: React.CSSProperties = {
  width: 20,
  height: 20,
  borderRadius: '50%',
  backgroundColor: '#5E6AD2',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const AI_HINT: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 8,
  padding: '10px 12px',
  margin: '16px 24px 0',
  borderRadius: 8,
  backgroundColor: 'rgba(94, 106, 210, 0.06)',
  border: '1px solid rgba(94, 106, 210, 0.12)',
};

const FOOTER: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: '16px 24px 20px',
  gap: 12,
};

const CANCEL_BTN: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 400,
  color: '#9A8E82',
  fontFamily: F,
  padding: 0,
};

const CREATE_BTN: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '10px 24px',
  borderRadius: 8,
  border: 'none',
  backgroundColor: '#2E2318',
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: 600,
  color: '#FDFCFB',
  fontFamily: F,
  lineHeight: '18px',
};
